import { faListCheck, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { memo, useContext, useMemo } from 'react';

import AssignedToTask from '@/components/AssignedToTask/AssignedToTask';
import { Color } from '@/constants/color';
import useAPI from '@/hooks/useAPI';
import PersonContext from '@/pages/Person/PersonContext';
import TimelineHeader from '@/pages/Timeline/components/TimelineHeader/TimelineHeader';
import TimestampAndActor from '@/pages/Timeline/components/TimestampAndActor/TimestampAndActor';
import { TimelineSubcomponentProps } from '@/pages/Timeline/types';
import WorkRepository from '@/repositories/WorkRepository';
import Badge from '@/ui/components/Badge/Badge';
import Button from '@/ui/components/Button/Button';
import Card from '@/ui/components/Card';
import DualTime from '@/ui/components/DualTime/DualTime';
import statusColor from '@/utils/statusColor';

const TaskEvent = ({ activityLog }: TimelineSubcomponentProps) => {
  const { actor, data: activityLogData, object_id, timestamp } = activityLog;
  const {
    person: { time_zone }
  } = useContext(PersonContext);
  const workRepository = WorkRepository();

  const {
    data: task,
    isFetching,
    refetch: getTask
  } = useAPI({
    queryKey: ['getItem', object_id],
    queryFn: async () => {
      return await workRepository.getItem(object_id);
    },
    enabled: false
  });

  const title = useMemo(() => {
    return `Task ${activityLogData?.status === 'closed' ? 'Completed' : 'Created'}`;
  }, [activityLogData?.status]);

  const taskDetails = () => {
    const tags = activityLogData?.tags?.length
      ? activityLogData.tags.map((tag: string) => (
          <Badge key={tag} color={Color.GRAY}>
            {tag}
          </Badge>
        ))
      : null;
    const status = activityLogData?.status ? (
      <Badge color={statusColor[activityLogData.status]}>{activityLogData.status}</Badge>
    ) : (
      <p>No status present</p>
    );

    const dueAt = task?.data?.due_at;
    const assignedUser = task?.data?.assigned_user;

    return (
      <div className="space-x-1 space-y-1 mb-1">
        {status}
        {tags}

        {task?.data && (
          <>
            {dueAt ? (
              <Badge color={Color.GRAY}>
                <>
                  <span className="mr-0.5">Due:</span>
                  <DualTime dateTime={new Date(dueAt)} memberTZ={time_zone} />
                </>
              </Badge>
            ) : (
              <Badge color={Color.GRAY}>Due: No due date</Badge>
            )}

            {assignedUser ? (
              <AssignedToTask item={task.data} />
            ) : (
              <Badge
                color={Color.GRAY}
                text={
                  <>
                    <FontAwesomeIcon className="mr-1" icon={faUser} />
                    No assigned user
                  </>
                }
              />
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div>
      <Card>
        <TimelineHeader icon={faListCheck} subtitle={activityLogData?.task_name} title={title} />

        {taskDetails()}

        {!task && (
          <Button
            color={Color.WHITE}
            loading={isFetching}
            loadingWithSpinner={false}
            onClick={() => getTask()}
            size="xs">
            Click for More Details
          </Button>
        )}
      </Card>

      <TimestampAndActor actor={actor} timestamp={timestamp} />
    </div>
  );
};

export default memo(TaskEvent);
